import React, { useState, useEffect, useCallback, useRef } from "react";
import { useLocation } from "react-router-dom";
import api from "../services/api";
import { Button, Input, Table, Modal, Select, Slider, Row, Col, Collapse, Checkbox, Dropdown } from "antd";
import { SearchOutlined, EllipsisOutlined, DoubleLeftOutlined, LeftOutlined, RightOutlined, DoubleRightOutlined, UpOutlined, DownOutlined, MoreOutlined } from "@ant-design/icons";
import user_image from "../images/user_img.jpg";
import PercentileCircle from "../components/PercentileCircle";
import '../styles/SearchFilter.css';
import magnifying from "../images/icons/Magnifying.svg";

interface Industry {
    id: number;
    name: string;
}

const TalentPool: React.FC = () => {
    const location = useLocation();
    console.log("TalentPool navigation state:", location.state);
    // Default filter values
    const defaultFilters = {
        industry: undefined,
        software: undefined,
        test: undefined,
        location: undefined,
        percentile: [0, 100],
        topSkills: [],
    };
    // Initialize filters with navigation state if present
    const [filters, setFilters] = useState<Record<string, any>>({
        ...defaultFilters,
        ...(location.state || {})
    });
    const [pendingFilters, setPendingFilters] = useState<Record<string, any>>({
        industry: undefined,
        software: undefined,
        test: undefined,
        location: undefined,
        percentile: [0, 100],
        topSkills: [],
    });
    const [viewMode, setViewMode] = useState<'list' | 'grid'>('grid');

    // Sorting state
    const [sortOption, setSortOption] = useState<'name' | 'test' | 'software' | 'location' | 'score'>('name');

    const [candidates, setCandidates] = useState<any[]>([]);
    const [loading, setLoading] = useState(false);

    // Sorting function for candidates
    const sortCandidates = useCallback((candidatesToSort: any[]) => {
        return [...candidatesToSort].sort((a, b) => {
            switch (sortOption) {
                case 'name':
                    // Primary: name, Secondary: percentile (highest first)
                    const nameComparison = a.name.localeCompare(b.name);
                    if (nameComparison !== 0) return nameComparison;
                    return (b.percentile || 0) - (a.percentile || 0);
                case 'test':
                    // Primary: assessment, Secondary: name
                    const testA = a.test || 'N/A';
                    const testB = b.test || 'N/A';
                    const testComparison = testA.localeCompare(testB);
                    if (testComparison !== 0) return testComparison;
                    return a.name.localeCompare(b.name);
                case 'software':
                    // Primary: software, Secondary: name
                    const softwareA = a.software || 'N/A';
                    const softwareB = b.software || 'N/A';
                    const softwareComparison = softwareA.localeCompare(softwareB);
                    if (softwareComparison !== 0) return softwareComparison;
                    return a.name.localeCompare(b.name);
                case 'location':
                    // Primary: location, Secondary: name
                    const locationA = a.location || 'N/A';
                    const locationB = b.location || 'N/A';
                    const locationComparison = locationA.localeCompare(locationB);
                    if (locationComparison !== 0) return locationComparison;
                    return a.name.localeCompare(b.name);
                case 'score':
                    // Primary: percentile (highest first), Secondary: name
                    const percentileA = a.percentile || 0;
                    const percentileB = b.percentile || 0;
                    const percentileComparison = percentileB - percentileA;
                    if (percentileComparison !== 0) return percentileComparison;
                    return a.name.localeCompare(b.name);
                default:
                    return 0;
            }
        });
    }, [sortOption]);
    const [searchInput, setSearchInput] = useState("");
    const [search, setSearch] = useState("");
    const searchTimeoutRef = React.useRef<NodeJS.Timeout | null>(null);
    const [filterModalOpen, setFilterModalOpen] = useState(false);
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(15);
    const [total, setTotal] = useState(0);

    // Industries data for sidebar filters
    const [industries, setIndustries] = useState<Industry[]>([]);
    const [locations, setLocations] = useState<string[]>([]);

    // Selected filter states for sidebar
    const [selectedIndustryIds, setSelectedIndustryIds] = useState<number[]>([]);
    const [selectedSoftwareIds, setSelectedSoftwareIds] = useState<number[]>([]);
    const [selectedAssessmentIds, setSelectedAssessmentIds] = useState<number[]>([]);
    const [selectedLocationIds, setSelectedLocationIds] = useState<string[]>([]);

    // Track if we've initialized from navigation state to avoid resetting user selections
    const hasInitializedFromNavState = useRef(false);

    const loadIndustries = async () => {
        try {
            const response = await api.get('industries/');
            setIndustries(response.data);
        } catch (error) {
            console.error('Failed to load industries:', error);
        }
    };

    const loadLocations = useCallback(async () => {
        try {
            // Get available talent pool candidates with the same base filters as main query
            // This ensures location options match what's actually visible
            console.log('Loading locations from /talent-pool/ endpoint...');
            const params = new URLSearchParams();
            params.append('page_size', '1000');

            // Apply same filters as main query (using filters state, not sidebar selections)
            if (filters.industry) params.append('industry', filters.industry.toString());
            if (filters.software) params.append('software', filters.software.toString());
            if (filters.test) params.append('test', filters.test.toString());
            // Don't apply location filter when loading locations
            if (filters.percentile) {
                params.append('percentile_min', filters.percentile[0].toString());
                params.append('percentile_max', filters.percentile[1].toString());
            }

            const response = await api.get(`/talent-pool/?${params.toString()}`);
            console.log('Locations API response:', response.data);
            const allCandidates = response.data.results;
            console.log('All candidates for location extraction:', allCandidates);

            // Extract unique non-empty locations from unique users (not per assessment assignment)
            const uniqueUserLocations = new Map<number, string>();
            allCandidates.forEach((candidate: any) => {
                if (candidate.location && candidate.location !== null && candidate.location.trim() !== '' && candidate.user_id) {
                    uniqueUserLocations.set(candidate.user_id, candidate.location.trim());
                }
            });

            const uniqueLocations = Array.from(new Set(uniqueUserLocations.values())).sort();

            console.log('User location map:', uniqueUserLocations);
            console.log('Extracted unique locations:', uniqueLocations);
            setLocations(uniqueLocations);
        } catch (error) {
            console.error('Failed to load locations:', error);
        }
    }, [filters.industry, filters.software, filters.test, filters.percentile]);

    const clearAllFilters = () => {
        setSelectedIndustryIds([]);
        setSelectedSoftwareIds([]);
        setSelectedAssessmentIds([]);
        setSelectedLocationIds([]);
        setFilters({
            industry: undefined,
            software: undefined,
            test: undefined,
            location: undefined,
            percentile: [0, 100],
            topSkills: [],
        });
        setPage(1);
    };

    useEffect(() => {
        loadIndustries();
        loadLocations();
    }, [loadLocations]);

    // Initialize sidebar selections from navigation state filters (only once)
    useEffect(() => {
        if (!hasInitializedFromNavState.current) {
            if (filters.industry) {
                setSelectedIndustryIds([filters.industry]);
            }
            if (filters.software) {
                setSelectedSoftwareIds([filters.software]);
            }
            if (filters.test) {
                setSelectedAssessmentIds([filters.test]);
            }
            hasInitializedFromNavState.current = true;
        }
    }, []); // Empty dependency array - only run once on mount

    // Reload locations when filter selections change
    useEffect(() => {
        loadLocations();
    }, [filters.industry, filters.software, filters.test, filters.percentile, loadLocations]);

    useEffect(() => {
        setLoading(true);
        // Build filter params from checkbox selections using URLSearchParams
        const params = new URLSearchParams();

        params.append('page', page.toString());
        params.append('page_size', pageSize.toString());

        if (search) params.append('search', search);

        // Add multiple values for each filter type (from sidebar selections)
        if (selectedIndustryIds.length > 0) {
            selectedIndustryIds.forEach(id => params.append('industry', id.toString()));
        }
        if (selectedSoftwareIds.length > 0) {
            selectedSoftwareIds.forEach(id => params.append('software', id.toString()));
        }
        if (selectedAssessmentIds.length > 0) {
            selectedAssessmentIds.forEach(id => params.append('test', id.toString()));
        }
        if (selectedLocationIds.length > 0) {
            selectedLocationIds.forEach(location => params.append('location', location));
        }

        // Add initial filters from navigation state (if no sidebar selections)
        if (selectedIndustryIds.length === 0 && filters.industry) {
            params.append('industry', filters.industry.toString());
        }
        if (selectedSoftwareIds.length === 0 && filters.software) {
            params.append('software', filters.software.toString());
        }
        if (selectedAssessmentIds.length === 0 && filters.test) {
            params.append('test', filters.test.toString());
        }

        if (filters.percentile) {
            params.append('percentile_min', filters.percentile[0].toString());
            params.append('percentile_max', filters.percentile[1].toString());
        }

        api.get(`/talent-pool/?${params.toString()}`)
            .then((response: { data: any }) => {
                const data = response.data;
                console.log('TalentPool API response:', data); // Debug backend data
                if (data && data.results) {
                    console.log('First result:', data.results[0]);
                    if (data.results.length > 0) {
                        console.log('First result keys:', Object.keys(data.results[0]));
                    }
                }
                const sortedCandidates = sortCandidates(data.results || []);
                setCandidates(sortedCandidates);
                setTotal(data.count || 0);
            })
            .catch((err: any) => {
                console.error('API error:', err);
                setCandidates([]);
                setTotal(0);
            })
            .finally(() => setLoading(false));
    }, [viewMode, page, pageSize, search, selectedIndustryIds, selectedSoftwareIds, selectedAssessmentIds, selectedLocationIds, filters.percentile, filters.industry, filters.software, filters.test, sortCandidates]);

    // Debounced search input: update search after 500ms of inactivity
    useEffect(() => {
        if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
        searchTimeoutRef.current = setTimeout(() => {
            setSearch(searchInput);
        }, 500);
        return () => {
            if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
        };
    }, [searchInput]);

    // Generate industry, software, and assessment options from candidates (use IDs for value, names for label)
    const industryOptions = Array.from(
        new Map(
            (candidates || [])
                .filter((c: any) => c.industry_id && c.industry)
                .map((c: any) => [c.industry_id, { value: c.industry_id, label: c.industry }])
        ).values()
    );
    const softwareOptions = Array.from(
        new Map(
            (candidates || [])
                .filter((c: any) => c.software_id && c.software)
                .map((c: any) => [c.software_id, { value: c.software_id, label: c.software }])
        ).values()
    );
    const testOptions = Array.from(
        new Map(
            (candidates || [])
                .filter((c: any) => c.test_id && c.test)
                .map((c: any) => [c.test_id, { value: c.test_id, label: c.test }])
        ).values()
    );

    useEffect(() => {
        console.log("Candidates for filter options:", candidates);
        if (candidates.length > 0) {
            console.log("Sample candidate:", candidates[0]);
            console.log("Sample candidate fields:", Object.keys(candidates[0]));
        }
        console.log("Industry options:", industryOptions);
        console.log("Software options:", softwareOptions);
        console.log("Assessment options:", testOptions);
    }, [candidates, industryOptions, softwareOptions, testOptions]);
    // Table columns for list view
    const columns = [
        {
            title: "Name",
            dataIndex: "name",
            key: "name",
            render: (name: string, row: any) => (
                <div style={{ display: 'flex', alignItems: 'center' }}>
                    <img src={row.profile_image ? row.profile_image : user_image} alt="Candidate" style={{ width: 40, height: 40, borderRadius: '50%', marginRight: 12, border: '2px solid #e5eaf2' }} />
                    <span style={{ fontWeight: 500, color: '#23244A' }}>{name}</span>
                </div>
            ),
        },
        {
            title: "Assessment",
            dataIndex: "test",
            key: "test",
            render: (test: string, row: any) => (
                <span style={{ fontSize: 14, fontWeight: 500 }}>{row.test || "N/A"}</span>
            ),
        },
        {
            title: "Software",
            dataIndex: "software",
            key: "software",
            render: (software: string, row: any) => (
                <span style={{ fontSize: 14, fontWeight: 500 }}>{row.software || "N/A"}</span>
            ),
        },
        {
            title: "Location",
            dataIndex: "location",
            key: "location",
            render: (location: string) => location || "Non disclosed",
        },
        {
            title: "Score",
            dataIndex: "percentile",
            key: "percentile",
            render: (percentile: number) => <PercentileCircle value={percentile} />, // assumes you have this component
        },
        {
            title: "Top Functions",
            dataIndex: "top_skills",
            key: "top_skills",
            render: (topSkills: string[], row: any) => {
                if (!Array.isArray(topSkills) || topSkills.length === 0) {
                    return <span style={{ fontSize: 14, color: '#999' }}>N/A</span>;
                }
                // Display first 2-3 skills, or all if there are only a few
                const displaySkills = topSkills.slice(0, 3);
                const hasMore = topSkills.length > 3;

                return (
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                        {displaySkills.map((skill, idx) => (
                            <span
                                key={idx}
                                style={{
                                    background: "#F4F8FF",
                                    color: "#8E99BA",
                                    border: "none",
                                    borderRadius: 999,
                                    fontSize: 10,
                                    fontWeight: 500,
                                    padding: "0px 15px",
                                    margin: 0,
                                    lineHeight: "1.2rem"
                                }}
                            >
                                {skill}
                            </span>
                        ))}
                        {hasMore && (
                            <span style={{ fontSize: 12, color: '#999', fontWeight: 500 }}>
                                +{topSkills.length - 3} more
                            </span>
                        )}
                    </div>
                );
            },
        },
        {
            title: '',
            key: 'actions',
            render: (_: any, record: any) => {
                const menuItems = [
                    {
                        key: 'profile',
                        label: 'See profile',
                        onClick: (e: any) => {
                            e?.domEvent?.stopPropagation();
                            window.location.href = `/candidate/profile/${record.user_id}`;
                        },
                    },
                    {
                        key: 'test-results',
                        label: 'See assessment results',
                        onClick: (e: any) => {
                            e?.domEvent?.stopPropagation();
                            // Use assignment ID (record.id) for assessment results
                            const url = `/candidate/test-result?assignment_id=${record.id}`;
                            console.log('Navigating to assessment results:', url);
                            window.location.href = url;
                        },
                    },
                ];

                return (
                    <div
                        onClick={(e) => e.stopPropagation()}
                        style={{ display: 'flex', justifyContent: 'center' }}
                    >
                        <Dropdown
                            menu={{ items: menuItems }}
                            placement="bottomRight"
                            trigger={['click']}
                        >
                            <span
                                style={{
                                    display: 'flex',
                                    justifyContent: 'center',
                                    alignItems: 'center',
                                    height: '100%',
                                    cursor: 'pointer',
                                    padding: '4px 8px',
                                    borderRadius: 4,
                                    transition: 'background-color 0.2s'
                                }}
                                onMouseEnter={(e) => (e.target as HTMLElement).style.backgroundColor = '#f5f5f5'}
                                onMouseLeave={(e) => (e.target as HTMLElement).style.backgroundColor = 'transparent'}
                            >
                                <EllipsisOutlined style={{ fontSize: 22, color: '#bfc5d2' }} />
                            </span>
                        </Dropdown>
                    </div>
                );
            },
        },
    ];

    // Main JSX return
    return (
        <div style={containerStyle}>
            <style>{`
                .custom-sort-dropdown .ant-select-item {
                    font-size: 12px !important;
                }
                .custom-sort-dropdown .ant-select-selection-item {
                    font-size: 12px !important;
                }
                .ant-select-selector {
                    font-size: 12px !important;
                }
            `}</style>
            <Row gutter={[8, 0]} style={{ margin: 0, gap: "30px" }}>
                {/* Filters Sidebar */}
                <Col xs={24} lg={6} style={{ height: '100vh', paddingLeft: 0, maxWidth: '260px', flex: '0 0 260px' }}>
                    <div style={filtersContainerStyle} className="search-filter">
                        <div style={filtersHeaderStyle}>
                            <h3 style={filtersTitleStyle}>Filters</h3>
                            <Button type="link" onClick={clearAllFilters} style={clearAllStyle}>
                                Clear all
                            </Button>
                        </div>

                        {/* Collapsible Filters */}
                        <Collapse
                            defaultActiveKey={['industries', 'software', 'tests', 'location']}
                            ghost
                            expandIconPosition="end"
                            expandIcon={({ isActive }) => isActive ? <UpOutlined /> : <DownOutlined />}
                            style={{ background: 'transparent' }}
                        >
                            {/* Industries Filter */}
                            <Collapse.Panel header="Industries" key="industries" style={panelStyle}>
                                <div style={checkboxGroupStyle}>
                                    {industries.map(industry => (
                                        <div key={industry.id} style={checkboxItemStyle}>
                                            <Checkbox
                                                checked={selectedIndustryIds.includes(industry.id)}
                                                onChange={(e) => {
                                                    if (e.target.checked) {
                                                        setSelectedIndustryIds(prev => [...prev, industry.id]);
                                                    } else {
                                                        setSelectedIndustryIds(prev => prev.filter(id => id !== industry.id));
                                                    }
                                                    setPage(1);
                                                }}
                                                style={checkboxStyle}
                                            >
                                                {industry.name}
                                            </Checkbox>
                                        </div>
                                    ))}
                                </div>
                                <Button type="link" onClick={() => {
                                    setSelectedIndustryIds([]);
                                    setFilters(prev => ({ ...prev, industry: undefined }));
                                    setPage(1);
                                }} style={clearFiltersStyle}>
                                    Clear filters
                                </Button>
                            </Collapse.Panel>

                            {/* Software Filter */}
                            <Collapse.Panel header="Software" key="software" style={panelStyle}>
                                <div style={checkboxGroupStyle}>
                                    {softwareOptions.map(software => (
                                        <div key={software.value} style={checkboxItemStyle}>
                                            <Checkbox
                                                checked={selectedSoftwareIds.includes(software.value)}
                                                onChange={(e) => {
                                                    if (e.target.checked) {
                                                        setSelectedSoftwareIds(prev => [...prev, software.value]);
                                                    } else {
                                                        setSelectedSoftwareIds(prev => prev.filter(id => id !== software.value));
                                                    }
                                                    setPage(1);
                                                }}
                                                style={checkboxStyle}
                                            >
                                                {software.label}
                                            </Checkbox>
                                        </div>
                                    ))}
                                </div>
                                <Button type="link" onClick={() => {
                                    setSelectedSoftwareIds([]);
                                    setFilters(prev => ({ ...prev, software: undefined }));
                                    setPage(1);
                                }} style={clearFiltersStyle}>
                                    Clear filters
                                </Button>
                            </Collapse.Panel>

                            {/* Assessments Filter */}
                            <Collapse.Panel header="Assessments" key="tests" style={panelStyle}>
                                <div style={checkboxGroupStyle}>
                                    {testOptions.map(test => (
                                        <div key={test.value} style={checkboxItemStyle}>
                                            <Checkbox
                                                checked={selectedAssessmentIds.includes(test.value)}
                                                onChange={(e) => {
                                                    if (e.target.checked) {
                                                        setSelectedAssessmentIds(prev => [...prev, test.value]);
                                                    } else {
                                                        setSelectedAssessmentIds(prev => prev.filter(id => id !== test.value));
                                                    }
                                                    setPage(1);
                                                }}
                                                style={checkboxStyle}
                                            >
                                                {test.label}
                                            </Checkbox>
                                        </div>
                                    ))}
                                </div>
                                <Button type="link" onClick={() => {
                                    setSelectedAssessmentIds([]);
                                    setFilters(prev => ({ ...prev, test: undefined }));
                                    setPage(1);
                                }} style={clearFiltersStyle}>
                                    Clear filters
                                </Button>
                            </Collapse.Panel>

                            {/* Location Filter */}
                            <Collapse.Panel header="Location" key="location" style={panelStyle}>
                                <div style={checkboxGroupStyle}>
                                    {locations.length > 0 ? (
                                        locations.map(location => (
                                            <div key={location} style={checkboxItemStyle}>
                                                <Checkbox
                                                    checked={selectedLocationIds.includes(location)}
                                                    onChange={(e) => {
                                                        if (e.target.checked) {
                                                            setSelectedLocationIds(prev => [...prev, location]);
                                                        } else {
                                                            setSelectedLocationIds(prev => prev.filter(loc => loc !== location));
                                                        }
                                                        setPage(1);
                                                    }}
                                                    style={checkboxStyle}
                                                >
                                                    {location}
                                                </Checkbox>
                                            </div>
                                        ))
                                    ) : (
                                        <div style={noDataMessageStyle}>
                                            No locations available
                                        </div>
                                    )}
                                </div>
                                {locations.length > 0 && (
                                    <Button type="link" onClick={() => {
                                        setSelectedLocationIds([]);
                                        setFilters(prev => ({ ...prev, location: undefined }));
                                        setPage(1);
                                    }} style={clearFiltersStyle}>
                                        Clear filters
                                    </Button>
                                )}
                            </Collapse.Panel>
                        </Collapse>
                    </div>
                </Col>

                {/* Main Content */}
                <Col xs={24} lg={20} style={{ paddingRight: 0, flex: '1', minWidth: 0 }}>
                    <div style={mainContentStyle}>
                        {/* Header Row 1: Title and Info Banner */}
                        <div style={headerRowStyle}>
                            <h2 style={titleStyle}>Talent pool</h2>
                            <div style={infoBannerStyle}>
                                Here, you can view all assessment-takers on the platform and view their profile and assessment history.
                            </div>
                        </div>

                        {/* Header Row 2: Search and Controls */}
                        <div style={controlsRowStyle}>
                            <div className="input-form" style={{ flex: 1, marginRight: 16 }}>
                                <Input
                                    placeholder="Search"
                                    value={searchInput}
                                    onChange={e => setSearchInput(e.target.value)}
                                    style={{
                                        borderRadius: 24,
                                        width: "100%",
                                        background: "#eaf2ff",
                                        border: "none",
                                        color: "#23244A",
                                        fontWeight: 500,
                                        height: 32,
                                        fontSize: 14,
                                        paddingLeft: 24,
                                        paddingRight: 24,
                                    }}
                                    suffix={
                                        searchInput ? (
                                            <span
                                                style={{ cursor: 'pointer', color: '#787A7D', marginRight: 4 }}
                                                onClick={() => setSearchInput("")}
                                            >
                                                ×
                                            </span>
                                        ) : (
                                            <img src={magnifying} style={{ height: 15, width: 15 }} />
                                        )
                                    }
                                />
                            </div>

                            <div style={rightControlsStyle}>
                                <div style={sortContainerStyle}>
                                    <Select
                                        value={sortOption}
                                        onChange={(value) => setSortOption(value)}
                                        style={{
                                            width: 180,
                                            borderRadius: 24,
                                            background: "#eaf2ff",
                                            border: "none",
                                            color: "#23244A",
                                            fontWeight: 500,
                                            height: 32,
                                            fontSize: '12px !important',
                                        }}
                                        size="large"
                                        bordered={false}
                                        dropdownStyle={{
                                            borderRadius: 8,
                                            boxShadow: "0 4px 12px rgba(0, 0, 0, 0.15)",
                                            minWidth: 180,
                                            fontSize: '12px !important',
                                        }}
                                        dropdownClassName="custom-sort-dropdown"
                                    >
                                        <Select.Option value="name">Sort by Name (A-Z)</Select.Option>
                                        <Select.Option value="test">Sort by Assessment (A-Z)</Select.Option>
                                        <Select.Option value="software">Sort by Software (A-Z)</Select.Option>
                                        <Select.Option value="location">Sort by Location (A-Z)</Select.Option>
                                        <Select.Option value="score">Sort by Score (High-Low)</Select.Option>
                                    </Select>
                                </div>
                                <div style={viewToggleStyle}>
                                    {/* List View Icon */}
                                    <Button
                                        type={viewMode === 'list' ? 'primary' : 'default'}
                                        size="small"
                                        className="toggle-button"
                                        style={{
                                            ...toggleButtonStyle,
                                            backgroundColor: viewMode === 'list' ? '#2563eb' : 'transparent',
                                            borderColor: 'transparent',
                                        }}
                                        onClick={() => setViewMode('list')}
                                    >
                                        <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                                            <rect x="2" y="3" width="12" height="1.5" fill={viewMode === 'list' ? '#fff' : '#666'} rx="0.75" />
                                            <rect x="2" y="7.25" width="12" height="1.5" fill={viewMode === 'list' ? '#fff' : '#666'} rx="0.75" />
                                            <rect x="2" y="11.5" width="12" height="1.5" fill={viewMode === 'list' ? '#fff' : '#666'} rx="0.75" />
                                        </svg>
                                    </Button>

                                    {/* Grid View Icon */}
                                    <Button
                                        type={viewMode === 'grid' ? 'primary' : 'default'}
                                        size="small"
                                        className="toggle-button"
                                        style={{
                                            ...toggleButtonStyle,
                                            backgroundColor: viewMode === 'grid' ? '#2563eb' : 'transparent',
                                            borderColor: 'transparent',
                                        }}
                                        onClick={() => setViewMode('grid')}
                                    >
                                        <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                                            <rect x="2" y="2" width="5" height="5" fill={viewMode === 'grid' ? '#fff' : '#666'} rx="1" />
                                            <rect x="9" y="2" width="5" height="5" fill={viewMode === 'grid' ? '#fff' : '#666'} rx="1" />
                                            <rect x="2" y="9" width="5" height="5" fill={viewMode === 'grid' ? '#fff' : '#666'} rx="1" />
                                            <rect x="9" y="9" width="5" height="5" fill={viewMode === 'grid' ? '#fff' : '#666'} rx="1" />
                                        </svg>
                                    </Button>
                                </div>
                            </div>
                        </div>

                        {/* Content Area */}
                        <div style={tableContainerStyle}>
                            {loading ? (
                                <div>Loading...</div>
                            ) : viewMode === 'list' ? (
                                <Table
                                    className="talent-pool-table"
                                    columns={columns}
                                    dataSource={candidates}
                                    rowKey={(row) => row.id}
                                    pagination={false}
                                    bordered
                                    loading={loading}
                                    locale={{ emptyText: 'No results. Try adjusting your filters.' }}
                                    onRow={(record) => ({
                                        onClick: () => { window.location.href = `/candidate/profile/${record.user_id}`; },
                                        style: { cursor: 'pointer' },
                                    })}
                                />
                            ) : viewMode === 'grid' ? (
                                candidates.length === 0 ? (
                                    <div style={{
                                        display: 'flex',
                                        flexDirection: 'column',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        padding: '60px 20px',
                                        textAlign: 'center'
                                    }}>
                                        <div style={{
                                            fontSize: '18px',
                                            fontWeight: 600,
                                            color: '#23244A',
                                            marginBottom: '12px'
                                        }}>
                                            No candidates found
                                        </div>
                                        <div style={{
                                            fontSize: '14px',
                                            color: '#888',
                                            marginBottom: '24px',
                                            maxWidth: '400px',
                                            lineHeight: '1.5'
                                        }}>
                                            The current filters didn't find any results. Try adjusting your filters or clear them to find more candidates.
                                        </div>
                                        <button
                                            onClick={clearAllFilters}
                                            style={{
                                                background: '#2563eb',
                                                color: 'white',
                                                border: 'none',
                                                borderRadius: '24px',
                                                padding: '12px 24px',
                                                fontSize: '14px',
                                                fontWeight: 600,
                                                cursor: 'pointer',
                                                transition: 'background-color 0.2s'
                                            }}
                                            onMouseEnter={(e) => (e.target as HTMLElement).style.backgroundColor = '#1d4ed8'}
                                            onMouseLeave={(e) => (e.target as HTMLElement).style.backgroundColor = '#2563eb'}
                                        >
                                            Clear all filters
                                        </button>
                                    </div>
                                ) : (
                                    <div style={{
                                        display: 'grid',
                                        gridTemplateColumns: 'repeat(3, minmax(270px, 1fr))',
                                        gap: '20px',
                                        marginBottom: 20
                                    }}>
                                        {candidates.map((c, idx) => (
                                            <div key={c.id} onClick={() => window.location.href = `/candidate/profile/${c.user_id}`}
                                                style={{ cursor: 'pointer', display: 'flex', justifyContent: 'center' }}>
                                                <div className="card" style={{
                                                    borderRadius: 18,
                                                    boxShadow: '0 2px 8px #e5eaf2',
                                                    border: 'none',
                                                    width: '100%',
                                                    padding: '18px 20px',
                                                    position: 'relative'
                                                }}>
                                                    {/* Vertical ellipsis in top right */}
                                                    <div style={{ position: 'absolute', top: 12, right: 12 }}>
                                                        <div onClick={(e) => e.stopPropagation()}>
                                                            <Dropdown
                                                                menu={{
                                                                    items: [
                                                                        {
                                                                            key: 'profile',
                                                                            label: 'See profile',
                                                                            onClick: (e: any) => {
                                                                                e?.domEvent?.stopPropagation();
                                                                                window.location.href = `/candidate/profile/${c.user_id}`;
                                                                            },
                                                                        },
                                                                        {
                                                                            key: 'test-results',
                                                                            label: 'See assessment results',
                                                                            onClick: (e: any) => {
                                                                                e?.domEvent?.stopPropagation();
                                                                                // Use assignment ID (c.id) for assessment results
                                                                                const url = `/candidate/test-result?assignment_id=${c.id}`;
                                                                                console.log('Navigating to assessment results:', url);
                                                                                window.location.href = url;
                                                                            },
                                                                        },
                                                                    ]
                                                                }}
                                                                placement="bottomRight"
                                                                trigger={['click']}
                                                            >
                                                                <span
                                                                    style={{
                                                                        cursor: 'pointer',
                                                                        padding: '4px 6px',
                                                                        borderRadius: 4,
                                                                        transition: 'background-color 0.2s',
                                                                        display: 'inline-block'
                                                                    }}
                                                                    onMouseEnter={(e) => (e.target as HTMLElement).style.backgroundColor = '#f5f5f5'}
                                                                    onMouseLeave={(e) => (e.target as HTMLElement).style.backgroundColor = 'transparent'}
                                                                >
                                                                    <MoreOutlined style={{ fontSize: 16, color: '#bfc5d2' }} />
                                                                </span>
                                                            </Dropdown>
                                                        </div>
                                                    </div>

                                                    {/* Main card content */}
                                                    <div style={{ display: 'flex', flexDirection: 'row', alignItems: 'center', gap: 16 }}>
                                                        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', marginRight: 14 }}>
                                                            <img src={c.profile_image || user_image} alt="Candidate" style={{ width: 54, height: 54, borderRadius: '50%', objectFit: 'cover', background: '#fff', marginBottom: 8 }} />
                                                            <PercentileCircle value={typeof c.percentile === 'number' ? c.percentile : 0} size={38} />
                                                        </div>
                                                        <div style={{ flex: 1, minWidth: 0 }}>
                                                            <div className="talent-pool-candidate-name" style={{ fontWeight: 700, fontSize: 17, color: '#23244A', marginBottom: 4, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{c.name}</div>
                                                            {/* Location instead of industry tag */}
                                                            <div style={{ fontSize: 12, color: '#888', fontWeight: 400, marginBottom: 6 }}>{c.location || "Location not disclosed"}</div>
                                                            <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                                                                <div style={{ fontSize: 12, color: '#23244A', fontWeight: 500 }}><span style={{ color: '#2563eb', fontWeight: 600 }}>Software:</span> {c.software || "N/A"}</div>
                                                                <div style={{ fontSize: 12, color: '#23244A', fontWeight: 500 }}><span style={{ color: '#2563eb', fontWeight: 600 }}>Assessment:</span> {c.test || "N/A"}</div>
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                )
                            ) : null
                            }

                            {/* Pagination Section */}
                            <div style={paginationContainerStyle}>
                                {/* Left side: Items per page and item range */}
                                <div style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '24px',
                                }}>
                                    <span style={{
                                        fontWeight: 400,
                                        fontSize: '14px',
                                        color: '#23275C',
                                    }}>Items per page:</span>
                                    <select
                                        style={{
                                            background: '#E5EBFA',
                                            border: 'none',
                                            borderRadius: '24px',
                                            padding: '8px 16px ',
                                            fontWeight: 600,
                                            fontSize: '14px',
                                            color: '#23275C',
                                            outline: 'none',
                                            cursor: 'pointer',
                                        }}
                                        value={pageSize}
                                        onChange={e => setPageSize(Number(e.target.value))}
                                    >
                                        {[15, 20, 50].map(size => (
                                            <option key={size} value={size}>{size}</option>
                                        ))}
                                    </select>
                                    <span style={{
                                        fontWeight: 400,
                                        fontSize: '14px',
                                        color: '#23275C',
                                    }}>{total === 0 ? '0 - 0 of 0 items' : `${(page - 1) * pageSize + 1} - ${Math.min(page * pageSize, total)} of ${total} items`}</span>
                                </div>
                                {/* Right side: Pagination arrows and page numbers */}
                                <div style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '8px',
                                }}>
                                    <button
                                        style={{
                                            background: '#F9FBFE',
                                            border: '1px solid #E6EBFA',
                                            borderRadius: '50%',
                                            width: '35px',
                                            height: '35px',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            color: '#97A8BC',
                                            fontWeight: 400,
                                            fontSize: '18px',
                                            cursor: page === 1 ? 'not-allowed' : 'pointer',
                                            opacity: page === 1 ? 0.5 : 1,
                                            boxShadow: '0 2px 8px 0 rgba(35, 39, 92, 0.04)',
                                        }}
                                        disabled={page === 1}
                                        onClick={() => setPage(1)}
                                    ><DoubleLeftOutlined /></button>
                                    <button
                                        style={{
                                            background: '#F9FBFE',
                                            border: '1px solid #E6EBFA',
                                            borderRadius: '50%',
                                            width: '35px',
                                            height: '35px',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            color: '#97A8BC',
                                            fontWeight: 400,
                                            fontSize: '18px',
                                            cursor: page === 1 ? 'not-allowed' : 'pointer',
                                            opacity: page === 1 ? 0.5 : 1,
                                            boxShadow: '0 2px 8px 0 rgba(35, 39, 92, 0.04)',
                                        }}
                                        disabled={page === 1}
                                        onClick={() => setPage(page - 1)}
                                    ><LeftOutlined /></button>
                                    {/* Page Numbers */}
                                    {Array.from({ length: Math.min(10, Math.ceil(total / pageSize)) }, (_, i) => (
                                        <button key={i + 1} onClick={() => setPage(i + 1)} style={{
                                            background: page === i + 1 ? '#eaf2ff' : 'transparent',
                                            color: page === i + 1 ? '#23244A' : '#A0AEC0',
                                            border: 'none',
                                            borderRadius: '50%',
                                            width: 35,
                                            height: 35,
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            fontWeight: page === i + 1 ? 700 : 500,
                                            fontSize: 16,
                                            boxShadow: 'none',
                                            margin: '0 2px',
                                            transition: 'background 0.2s',
                                        }}>{i + 1}</button>
                                    ))}
                                    {Math.ceil(total / pageSize) > 10 && <span style={{ color: '#A0AEC0', fontWeight: 500, margin: '0 8px', fontSize: 20 }}>...</span>}
                                    {Math.ceil(total / pageSize) > 10 && (
                                        <button key={Math.ceil(total / pageSize)} onClick={() => setPage(Math.ceil(total / pageSize))} style={{
                                            background: page === Math.ceil(total / pageSize) ? '#eaf2ff' : 'transparent',
                                            color: page === Math.ceil(total / pageSize) ? '#23244A' : '#A0AEC0',
                                            border: 'none',
                                            borderRadius: '50%',
                                            width: 35,
                                            height: 35,
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            fontWeight: page === Math.ceil(total / pageSize) ? 700 : 500,
                                            fontSize: 20,
                                            boxShadow: 'none',
                                            margin: '0 2px',
                                            transition: 'background 0.2s',
                                        }}>{Math.ceil(total / pageSize)}</button>
                                    )}
                                    <button disabled={page === Math.ceil(total / pageSize) || total === 0} onClick={() => setPage(page + 1)} style={{ background: '#F9FBFE', color: '#97A8BC', border: '1px solid #E6EBFA', borderRadius: '50%', width: '35px', height: '35px', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 18, opacity: (page === Math.ceil(total / pageSize) || total === 0) ? 0.4 : 1, transition: 'background 0.2s' }}><RightOutlined /></button>
                                    <button disabled={page === Math.ceil(total / pageSize) || total === 0} onClick={() => setPage(Math.ceil(total / pageSize))} style={{ background: '#F9FBFE', color: '#97A8BC', border: '1px solid #E6EBFA', borderRadius: '50%', width: '35px', height: '35px', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 18, opacity: (page === Math.ceil(total / pageSize) || total === 0) ? 0.4 : 1, transition: 'background 0.2s' }}><DoubleRightOutlined /></button>
                                </div>
                            </div>
                        </div>
                    </div>
                </Col>
            </Row>

            {/* Legacy Filter Modal - Keep for now */}
            <Modal open={filterModalOpen} onCancel={() => setFilterModalOpen(false)} title="Filters" footer={null} centered>
                <div style={{ marginBottom: 16 }}>
                    <label>Industry</label>
                    <Select
                        style={{ width: '100%' }}
                        placeholder="Select industry"
                        value={pendingFilters.industry}
                        onChange={val => setPendingFilters(f => ({ ...f, industry: val }))}
                        allowClear
                        options={industryOptions}
                        notFoundContent="No industry data"
                    />
                </div>
                <div style={{ marginBottom: 16 }}>
                    <label>Software</label>
                    <Select
                        style={{ width: '100%' }}
                        placeholder="Select software"
                        value={pendingFilters.software}
                        onChange={val => setPendingFilters(f => ({ ...f, software: val }))}
                        allowClear
                        options={softwareOptions}
                        notFoundContent="No software data"
                    />
                </div>
                <div style={{ marginBottom: 16 }}>
                    <label>Assessment</label>
                    <Select
                        style={{ width: '100%' }}
                        placeholder="Select assessment"
                        value={pendingFilters.test}
                        onChange={val => setPendingFilters(f => ({ ...f, test: val }))}
                        allowClear
                        options={testOptions}
                        notFoundContent="No assessment data"
                    />
                </div>
                <div style={{ marginBottom: 16 }}>
                    <label>Location</label>
                    <Select
                        style={{ width: '100%' }}
                        placeholder="Select location"
                        value={pendingFilters.location}
                        onChange={val => setPendingFilters(f => ({ ...f, location: val }))}
                        allowClear
                        options={Array.from(new Set(candidates.map(c => ({ value: c.location, label: c.location })))).filter(o => o.value)}
                        notFoundContent="No location data"
                    />
                </div>
                <div style={{ marginBottom: 16 }}>
                    <label>Percentile</label>
                    <Slider
                        range
                        min={0}
                        max={100}
                        value={Array.isArray(pendingFilters.percentile) && pendingFilters.percentile.length === 2 ? pendingFilters.percentile : [0, 100]}
                        onChange={val => {
                            if (Array.isArray(val) && val.length === 2 && (val[0] !== pendingFilters.percentile[0] || val[1] !== pendingFilters.percentile[1])) {
                                setPendingFilters(f => ({ ...f, percentile: val }));
                            }
                        }}
                        marks={{ 0: '0', 100: '100' }}
                    />
                </div>
                <div style={{ marginBottom: 16 }}>
                    <label>Top Skills</label>
                    <Select
                        mode="multiple"
                        style={{ width: '100%' }}
                        placeholder="Select top functions"
                        value={pendingFilters.topSkills}
                        onChange={val => setPendingFilters((f: any) => ({ ...f, topSkills: val }))}
                        allowClear
                        options={Array.from(new Set((Array.isArray(candidates) ? candidates : []).flatMap((c: any) => Array.isArray(c.top_skills) ? c.top_skills : [])))
                            .filter((skill: any) => typeof skill === 'string' && skill)
                            .map((skill: any) => ({ value: String(skill), label: String(skill) }))}
                        notFoundContent="No top functions data"
                    />
                </div>
                <div style={{ textAlign: 'right' }}>
                    <Button type="primary" onClick={() => {
                        // Only update filters if values have changed to avoid infinite loop
                        setFilters(prev => {
                            // Compare each key
                            let changed = false;
                            for (const key of Object.keys(pendingFilters)) {
                                if (JSON.stringify(prev[key]) !== JSON.stringify(pendingFilters[key])) {
                                    changed = true;
                                    break;
                                }
                            }
                            if (changed) {
                                return { ...pendingFilters };
                            } else {
                                return prev;
                            }
                        });
                        setFilterModalOpen(false);
                    }}>Apply</Button>
                    <Button style={{ marginLeft: 8 }} onClick={() => { setPendingFilters({ industry: undefined, location: undefined, percentile: [0, 100], topSkills: [] }); }}>Clear All</Button>
                </div>
            </Modal>
        </div>
    );
};

// Styles
const containerStyle: React.CSSProperties = {
    height: "100vh",
    display: 'flex',
    flexDirection: 'column',
};

const filtersContainerStyle: React.CSSProperties = {
    backgroundColor: '#fff',
    borderRadius: 8,
    padding: 20,
    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.06)',
    overflowY: 'auto',
    maxWidth: '260px',
    width: '100%',
};

const mainContentStyle: React.CSSProperties = {
    minHeight: 'calc(100vh - 80px)', // Account for header height
    display: 'flex',
    flexDirection: 'column',
    width: '100%',
    paddingBottom: '40px', // Add bottom padding for pagination
};

const tableContainerStyle: React.CSSProperties = {
    display: 'flex',
    flexDirection: 'column',
    minHeight: 0,
    width: '100%',
    flex: 1,
    padding: "32px 0px 0px 0px",
    overflow: 'visible' // Allow content to be visible
};

const paginationContainerStyle: React.CSSProperties = {
    width: '100%',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginTop: '16px',
    marginBottom: '20px',
    maxWidth: '100%'
};

const filtersHeaderStyle: React.CSSProperties = {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 20,
};

const filtersTitleStyle: React.CSSProperties = {
    fontSize: 18,
    fontWeight: 600,
    color: '#1a1a1a',
    margin: 0,
};

const clearAllStyle: React.CSSProperties = {
    color: '#1677ff',
    padding: 0,
    fontSize: 14,
    fontWeight: 500,
};

const headerRowStyle: React.CSSProperties = {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 16,
};

const controlsRowStyle: React.CSSProperties = {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
};

const titleStyle: React.CSSProperties = {
    fontSize: 24,
    fontWeight: 600,
    color: '#1a1a1a',
    margin: 0,
};

// Collapsible Filter Styles
const panelStyle: React.CSSProperties = {
    backgroundColor: 'transparent',
    border: 'none',
    borderRadius: 0,
};

const checkboxGroupStyle: React.CSSProperties = {
    display: 'flex',
    flexDirection: 'column',
    gap: 12,
    marginBottom: 16,
};

const checkboxItemStyle: React.CSSProperties = {
    display: 'flex',
    alignItems: 'center',
};

const checkboxStyle: React.CSSProperties = {
    fontSize: 14,
};

const clearFiltersStyle: React.CSSProperties = {
    color: '#1677ff',
    padding: 0,
    fontSize: 14,
    fontWeight: 500,
};

const noDataMessageStyle: React.CSSProperties = {
    fontSize: 14,
    color: '#999',
    fontStyle: 'italic',
    padding: '12px 0',
};

const infoBannerStyle: React.CSSProperties = {
    backgroundColor: '#e6fffa',
    color: '#00b8a3',
    padding: '8px 20px',
    borderRadius: 8,
    fontSize: 14,
    fontWeight: 500,
    border: '1px solid #b3f5ec',
    marginLeft: 16,
    flex: 1,
    height: '32px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    textAlign: 'center',
    boxSizing: 'border-box',
};

const rightControlsStyle: React.CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    gap: 16,
};

const viewToggleStyle: React.CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    gap: 2,
    border: '1px solid #d9d9d9',
    borderRadius: 6,
    padding: 2,
    background: '#fff',
};

const toggleButtonStyle: React.CSSProperties = {
    border: 'none',
    borderRadius: 4,
    width: 32,
    height: 32,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: 0,
    minWidth: 32,
};

const sortContainerStyle: React.CSSProperties = {
    display: 'flex',
    alignItems: 'center',
};

export default TalentPool;